/**
 * Networked game session: fixed 60 Hz local prediction of the WHOLE room (own
 * car from live inputs, remote cars dead-reckoned with their last server-applied
 * input — one coherent timeline, see net/prediction.ts), follow camera solo /
 * spectacle camera with several players, minimap, official server lap timing.
 */

import type {
  CarConfig,
  CarInput,
  CarSnapshot,
  CarState,
  CircuitDescriptor,
  GhostData,
  GhostScope,
  PlayerPublicInfo,
  RaceStatus,
  ServerMessage,
  Standing,
  SurfaceConfigs,
  SurfaceId,
  Track,
  TrackSample,
} from '@microligue/shared';
import {
  DT,
  NEUTRAL_INPUT,
  TICK_RATE,
  closestPoint,
  computeCheckpoints,
  crossedGateBackward,
  crossedGateForward,
  defaultCarConfig,
  forwardArcDelta,
  lerpAngle,
} from '@microligue/shared';
import { ghostPoseAt, ghostStartPose } from './ghost';
import type { RaceUi } from './race-ui';
import type { Connection } from '../net/connection';
import { ServerClock } from '../net/interpolation';
import { WorldPredictor } from '../net/prediction';
import type { CameraLike } from './camera';
import { FollowCamera, SpectacleCamera } from './camera';
import type { CameraConfig } from './camera';
import type { DisplaySettings } from './display';
import {
  AutoEcoGovernor,
  defaultDisplaySettings,
  effectiveHalfRate,
  effectiveRenderScale,
} from './display';
import type { Hud } from './hud';
import { formatLapTime } from './hud';
import type { KeyboardInput } from './input';
import { Minimap } from './minimap';
import type { RenderCar } from './render';
import { Renderer } from './render';
import type { TuningPanel } from './tuning';

const MAX_FRAME_SECONDS = 0.25;
const MS_PER_TICK = 1000 / TICK_RATE;
const TELEPORT_ARC = 60;

/** Client mirror of the server's checkpoint-based lap tracking. */
interface LapState {
  /** Lap start in fractional ticks; null until the first line crossing. */
  startTick: number | null;
  nextCp: number;
  prevS: number;
  forwardEma: number;
  headingDot: number;
}

interface RemotePlayer {
  info: PlayerPublicInfo;
}

export class NetGame {
  private readonly renderer: Renderer;
  private readonly minimap: Minimap;
  private readonly followCamera: FollowCamera;
  private readonly spectacleCamera: SpectacleCamera;
  private readonly predictor: WorldPredictor;
  private readonly clock = new ServerClock();
  private readonly remotes = new Map<number, RemotePlayer>();
  private readonly lap: LapState;

  private accumulator = 0;
  /** Horloge d'AFFICHAGE (rAF) — télémétrie des gels d'image uniquement. */
  private lastFrameAt: number | null = null;
  /** Horloge de SIMULATION — avancée par les frames ET le tick de secours. */
  private lastSimAt: number | null = null;
  private tickCount = 0;
  /** Simulation pace factor (>1 = tick slightly slower). See updatePace. */
  private pace = 1;
  private paceErrEma = 0;
  /** Newest unapplied snapshot — applied once per frame (jank coalescing). */
  private queuedSnapshot: Extract<ServerMessage, { type: 'snapshot' }> | null = null;
  /** Own state before the latest tick — for render interpolation. */
  private prevOwnState: CarState;
  private pendingReset = false;
  private frameCounter = 0;
  private fpsWindowStart = 0;
  /** Mode éco 30 img/s : une frame rAF sur deux saute le RENDU (jamais la sim). */
  private renderParity = false;
  /** dt cumulé depuis le dernier rendu — les caméras lissent sur ce pas-là. */
  private camDt = 0;
  /** Gouverneur éco automatique — règles et niveaux dans display.ts. */
  private readonly ecoGovernor = new AutoEcoGovernor();
  /** Gels visibles > 100 ms dans la fenêtre FPS courante (500 ms). */
  private windowFreezes = 0;
  /** La fenêtre courante contient un retour d'onglet caché : jugement suspendu. */
  private windowHadHiddenReturn = false;
  /** Plus long trou VISIBLE entre deux frames de la fenêtre FPS (non plafonné). */
  private frameGapMaxMs = 0;
  /**
   * Vrai juste après un retour d'onglet caché/masqué : le navigateur suspend
   * l'animation d'un onglet caché, donc le premier écart de frame au retour
   * mesure l'ABSENCE (alt-tab), pas un gel du jeu. Sans cette distinction, un
   * passage de 3 s dans un autre onglet s'affichait « gel max 3000 ms ».
   */
  private nextGapIsHidden = false;
  private readonly onVisibility = (): void => {
    if (document.visibilityState === 'visible') {
      this.nextGapIsHidden = true;
      // Le heartbeat aussi repart d'ici : son premier battement au retour ne
      // doit pas compter l'absence (timers throttlés onglet caché).
      this.heartbeatLastAt = performance.now();
    }
  };
  /** --- Agrégats du rapport de fluidité (perfReport, ~30 s) --- */
  private reportStartAt: number | null = null;
  private repFpsSum = 0;
  private repFpsWindows = 0;
  private repFpsMin = Infinity;
  private repGapVisMax = 0;
  private repGapHidMax = 0;
  private repFreezes100 = 0;
  private repHeapMaxMb = -1;
  private repFileMax = 0;
  /**
   * Discriminant gel JS / gel graphique. Le heartbeat setInterval(50 ms) gèle
   * avec le THREAD JS (swap, antivirus, long task…) mais continue de battre
   * quand seul le pipeline GPU/compositeur cale (rAF arrêté, timers vivants) :
   * timerGap ≈ rafGap ⇒ thread bloqué ; timerGap ≪ rafGap ⇒ chaîne graphique.
   */
  private repTimerGapMax = 0;
  private repLongTasks = 0;
  private repLongTaskMax = 0;
  /** Ticks simulés par le secours pendant que le rAF était affamé. */
  private repStarvedTicks = 0;
  private heartbeatId: number | null = null;
  private heartbeatLastAt = 0;
  private longTaskObserver: PerformanceObserver | null = null;
  private rafId: number | null = null;
  private stopped = false;
  /** Latest processed snapshot tick — late deliveries (jitter) are dropped. */
  private lastSnapshotTick: number;
  private cameraMode: 'follow' | 'spectacle' = 'follow';
  /** Race phase, standings and gaps as broadcast by the server. */
  private race: RaceStatus;
  private standings: Standing[] = [];
  private gapAheadMs: number | null = null;
  private gapBehindMs: number | null = null;
  /** Every player in the room (self included) — feeds the waiting overlay. */
  private readonly roster = new Map<number, PlayerPublicInfo>();

  private readonly checkpoints: number[];
  private readonly circuitName: string;
  private readonly recordMs: number | null;
  private readonly recordBy: string | null;
  /** Best personal time currently displayed — only ever improves. */
  private shownPbMs: number | null = null;
  /** Best valid lap of the current race (SPEC §10) — reset at each GO. */
  private raceBestMs: number | null = null;

  /** --- Time-attack ghost (G key, waiting room only) --- */
  /** Fetched ghosts by scope; null = server answered "nothing recorded". */
  private readonly ghostCache = new Map<GhostScope, GhostData | null>();
  private ghostScope: GhostScope | null = null;
  /** The server silently drops fetches under 1/s — queue instead of losing. */
  private ghostFetchSentAt = -Infinity;
  private ghostFetchQueued: GhostScope | null = null;
  private readonly ghostDesign = defaultCarConfig('#aab2c4');

  constructor(
    private readonly canvas: HTMLCanvasElement,
    minimapCanvas: HTMLCanvasElement,
    private readonly track: Track,
    circuit: CircuitDescriptor,
    private readonly surfaces: SurfaceConfigs,
    cameraConfig: CameraConfig,
    private readonly connection: Connection,
    private readonly input: KeyboardInput,
    private readonly hud: Hud,
    private readonly panel: TuningPanel,
    private readonly raceUi: RaceUi,
    private readonly selfId: number,
    players: PlayerPublicInfo[],
    initialCars: CarSnapshot[],
    initialTick: number,
    personalBestMs: number | null,
    ownLap: { elapsedSteps: number; nextCp: number } | null,
    initialRace: RaceStatus,
    private readonly selfInfo: { pseudo: string; design: CarConfig },
    initialSurfaceOverride: SurfaceId | null = null,
    /** Réglages d'affichage locaux (mode éco) — objet PARTAGÉ avec le panneau,
     * relu à chaque frame : un changement s'applique immédiatement. */
    private readonly display: DisplaySettings = defaultDisplaySettings(),
  ) {
    this.renderer = new Renderer(canvas, track);
    this.minimap = new Minimap(minimapCanvas, track);
    this.followCamera = new FollowCamera(cameraConfig);
    this.spectacleCamera = new SpectacleCamera(track.bounds);

    // Start prediction from the authoritative initial state (grid slot).
    const own = initialCars.find((c) => c.id === selfId);
    const spawn: CarState = own
      ? { x: own.x, y: own.y, angle: own.angle, vx: own.vx, vy: own.vy }
      : { x: track.spawn.x, y: track.spawn.y, angle: track.spawn.angle, vx: 0, vy: 0 };
    this.predictor = new WorldPredictor(track, surfaces, spawn, selfId);
    this.predictor.overrideSurface = initialSurfaceOverride;
    this.renderer.setSurfaceOverride(initialSurfaceOverride);
    // The whole-circuit flip is a test-track tuning aid: only offer it on the
    // builtin circuit, and seed the panel with the room's current override.
    this.panel.setSurfaceOverrideAvailable(circuit.builtin);
    this.panel.setSurfaceOverride(initialSurfaceOverride);
    this.prevOwnState = { ...spawn };
    this.followCamera.snapTo(spawn);

    this.race = initialRace;
    // The whole-circuit flip is refused mid-race (records stay authentic), so
    // lock its button unless we're in the waiting room (échauffement libre).
    this.panel.setRaceActive(initialRace.state !== 'waiting');
    for (const p of players) {
      this.roster.set(p.id, { ...p });
      if (p.id !== selfId) {
        this.remotes.set(p.id, { info: p });
        this.predictor.addRemote(p.id);
      }
    }
    // Seed the whole predicted world (remote states + held inputs) exactly.
    this.predictor.applySnapshot(initialCars, 0);
    this.lastSnapshotTick = initialTick;
    this.hud.setPlayers(this.playersForHud());
    this.checkpoints = computeCheckpoints(track);
    this.circuitName = circuit.name;
    this.recordMs = circuit.recordMs;
    this.recordBy = circuit.recordBy;
    this.hud.resetLapDisplays();
    this.hud.setRecord(circuit.recordMs, circuit.recordBy);
    this.shownPbMs = personalBestMs;
    this.hud.setPersonalBest(personalBestMs);

    this.lap = {
      // A reconnection mid-lap restores the running timer (steps ≡ ticks here).
      startTick: ownLap ? -ownLap.elapsedSteps : null,
      nextCp: ownLap ? ownLap.nextCp : 0,
      prevS: closestPoint(track, spawn).s,
      forwardEma: 0,
      headingDot: 1,
    };
    this.refreshRaceUi();
  }

  /** True while the grid countdown locks the controls (no false start). */
  private get controlsLocked(): boolean {
    if (this.race.state !== 'countdown') return false;
    const now = this.clock.serverTick();
    // Unlock exactly at the server's GO tick, without waiting for its message
    // (which would cost us our own ping).
    return now === null || now < this.race.goTick;
  }

  private refreshRaceUi(): void {
    switch (this.race.state) {
      case 'waiting':
        this.raceUi.showWaiting(
          this.race,
          [...this.roster.values()].sort((a, b) => a.id - b.id),
          this.selfId,
          this.circuitName,
        );
        this.hud.setRaceInfo(null);
        break;
      case 'countdown':
        break; // driven per-frame by the estimated clock
      case 'racing':
        this.raceUi.hide();
        break;
      case 'finished':
        break; // the results overlay is shown by the raceResults message
    }
  }

  /**
   * UNE SEULE horloge (performance.now) pour toutes les datations. L'horodatage
   * rAF est une horloge DIFFÉRENTE (début de vsync) : au réveil d'un calage
   * compositeur il peut être ANTÉRIEUR au dernier tick de secours — un dt
   * négatif en sortait, et decayError (exponentielle) le transformait en
   * explosion des offsets : voiture catapultée hors du circuit (bug propriétaire
   * du 19/07). L'argument rAF est donc ignoré partout.
   */
  start(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibility);
    }
    // Heartbeat du thread JS (voir repTimerGapMax). Un onglet caché throttle
    // les timers à 1/s : ces écarts-là sont ignorés via nextGapIsHidden.
    this.heartbeatLastAt = performance.now();
    this.heartbeatId = window.setInterval(() => {
      const now = performance.now();
      const gap = now - this.heartbeatLastAt;
      this.heartbeatLastAt = now;
      if (document.visibilityState !== 'visible') return;
      if (gap > this.repTimerGapMax) this.repTimerGapMax = gap;
      // Tick de secours : le compositeur affame le rAF (> 100 ms sans frame)
      // alors que le thread JS bat — la simulation continue sans l'affichage.
      if (
        !this.stopped &&
        this.lastFrameAt !== null &&
        now - this.lastFrameAt > 100 &&
        this.lastSimAt !== null &&
        now - this.lastSimAt > 40
      ) {
        this.advanceSimulation(now, true);
      }
    }, 50);
    try {
      this.longTaskObserver = new PerformanceObserver((list) => {
        for (const e of list.getEntries()) {
          this.repLongTasks++;
          if (e.duration > this.repLongTaskMax) this.repLongTaskMax = e.duration;
        }
      });
      this.longTaskObserver.observe({ type: 'longtask', buffered: false });
    } catch {
      this.longTaskObserver = null; // navigateur sans Long Tasks API
    }
    this.rafId = requestAnimationFrame(() => this.frame(performance.now()));
  }

  stop(): void {
    this.stopped = true;
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
    if (this.heartbeatId !== null) {
      window.clearInterval(this.heartbeatId);
      this.heartbeatId = null;
    }
    this.longTaskObserver?.disconnect();
    this.longTaskObserver = null;
    // Dernier rapport de fluidité de la session (s'il couvre assez de temps).
    if (this.reportStartAt !== null && performance.now() - this.reportStartAt >= 5000) {
      this.sendPerfReport(performance.now());
    }
  }

  resize(): void {
    this.renderer.resize();
  }

  /** Server messages routed here while a session is active. */
  handleMessage(msg: ServerMessage): void {
    switch (msg.type) {
      case 'snapshot': {
        // The latency simulator (and real jitter) can deliver snapshots out of
        // order: rewinding the predictor onto an older state would replay an
        // incomplete pending list. Only ever move forward.
        if (msg.tick <= this.lastSnapshotTick) break;
        this.clock.onSnapshot(msg.tick);
        // COALESCE: only the NEWEST snapshot is kept, and it is applied at the
        // start of the next frame — never here in the WS handler. After a
        // main-thread freeze (GC, throttled tab, weak machine), the queued-up
        // snapshots used to be applied in one burst, each with a stale full
        // replay: the janky client saw its OPPONENT leap around more and more
        // (soak-net JANK bench: |err| 2 → 78 px, 40 snaps). Applying one per
        // frame keeps corrections fresh and bounded on any machine.
        if (!this.queuedSnapshot || msg.tick > this.queuedSnapshot.tick) {
          this.queuedSnapshot = msg;
        }
        break;
      }
      case 'surfaceOverride':
        // Server confirmed the whole-circuit flip (or its reset): realign the
        // local prediction, repaint the track, and reflect it in the tuning panel.
        this.predictor.overrideSurface = msg.surface;
        this.renderer.setSurfaceOverride(msg.surface);
        this.panel.setSurfaceOverride(msg.surface);
        break;
      case 'playerJoined':
        if (msg.player.id !== this.selfId) {
          this.remotes.set(msg.player.id, { info: msg.player });
          this.predictor.addRemote(msg.player.id);
        }
        this.roster.set(msg.player.id, { ...msg.player });
        this.hud.setPlayers(this.playersForHud());
        if (this.race.state === 'waiting') this.refreshRaceUi();
        break;
      case 'playerLeft':
        this.remotes.delete(msg.playerId);
        this.predictor.removeRemote(msg.playerId);
        this.roster.delete(msg.playerId);
        this.hud.setPlayers(this.playersForHud());
        if (this.race.state === 'waiting') this.refreshRaceUi();
        break;
      case 'playerConnection': {
        const remote = this.remotes.get(msg.playerId);
        if (remote) remote.info.connected = msg.connected;
        const rosterEntry = this.roster.get(msg.playerId);
        if (rosterEntry) rosterEntry.connected = msg.connected;
        this.hud.setPlayers(this.playersForHud());
        if (this.race.state === 'waiting') this.refreshRaceUi();
        break;
      }
      case 'raceState': {
        const previous = this.race.state;
        this.race = msg.race;
        // Lock the whole-circuit flip once a race starts; unlock back in the
        // waiting room. (The server also drops any active override on race start.)
        this.panel.setRaceActive(msg.race.state !== 'waiting');
        if (msg.race.state === 'countdown' && previous !== 'countdown') {
          // The server just teleported everyone onto the grid: drop the unacked
          // inputs (they would fight the neutralized server state) and rearm the
          // lap mirror on the grid.
          this.predictor.clearPending();
          this.standings = [];
          this.gapAheadMs = null;
          this.gapBehindMs = null;
          this.lap.startTick = null;
          this.lap.nextCp = 0;
          this.raceBestMs = null;
          this.hud.resetLapDisplays();
          this.hud.setRecord(this.recordMs, this.recordBy);
          this.hud.setPersonalBest(this.shownPbMs);
        }
        if (msg.race.state === 'racing' && previous !== 'racing') {
          this.hud.toast('GO !');
        }
        // Back to a non-racing phase: the best-lap line shows the all-time PB.
        if (msg.race.state !== 'racing' && previous === 'racing') {
          this.hud.setPersonalBest(this.shownPbMs);
        }
        this.refreshRaceUi();
        break;
      }
      case 'raceResults':
        this.raceUi.showResults(
          msg.laps,
          msg.results,
          this.selfId,
          this.selfId === this.race.hostId,
        );
        this.hud.setRaceInfo(null);
        this.hud.setPersonalBest(this.shownPbMs);
        break;
      case 'lapDone': {
        this.hud.setLastLap(msg.timeMs, msg.valid);
        const incoming = msg.personalBestMs ?? msg.sessionBestMs;
        if (incoming !== null && (this.shownPbMs === null || incoming < this.shownPbMs)) {
          this.shownPbMs = incoming;
          // A new personal best means a new personal ghost lap on the server.
          this.ghostCache.delete('personal');
          if (this.ghostScope === 'personal') this.requestGhost('personal');
        }
        if (this.race.state === 'racing') {
          // SPEC §10: during a race, the best-lap line is the best lap OF THE RACE.
          if (msg.valid && (this.raceBestMs === null || msg.timeMs < this.raceBestMs)) {
            this.raceBestMs = msg.timeMs;
          }
          this.hud.setPersonalBest(this.raceBestMs, 'course');
        } else {
          this.hud.setPersonalBest(this.shownPbMs);
        }
        if (!msg.valid) {
          this.hud.toast('Tour invalide — passez par tous les points de passage', 'invalid');
        }
        break;
      }
      case 'record':
        this.hud.setRecord(msg.timeMs, msg.pseudo);
        this.hud.toast(`🏆 Record du circuit battu par ${msg.pseudo} !`, 'record');
        // The record ghost just changed hands: drop the cache (refetched if
        // the record ghost is on screen).
        this.ghostCache.delete('record');
        if (this.ghostScope === 'record') this.requestGhost('record');
        break;
      case 'ghost': {
        this.ghostCache.set(msg.scope, msg.ghost);
        if (msg.scope === this.ghostScope) this.announceGhost(msg.ghost, msg.scope);
        break;
      }
      default:
        break;
    }
  }

  /**
   * Clock-skew servo, updated once per snapshot. Target backlog = the inputs
   * genuinely in flight (≈ RTT) plus a 2-tick server jitter buffer; the pace
   * leans against the (smoothed) deviation, bounded at ±0.64 % — invisible to
   * the player, but it cancels any systematic clock drift within seconds.
   *
   * The RTT here is the WINDOWED-MINIMUM (`basePingMs`), not the displayed
   * EWMA: main-thread freezes inflate measured pongs, and an inflated target
   * made the servo GROW the backlog (longer replays, larger remote
   * extrapolation) precisely on the machines already struggling.
   */
  private updatePace(): void {
    const rttTicks = (this.connection.basePingMs / 1000) * TICK_RATE;
    const target = rttTicks + 2;
    const err = this.predictor.pendingLength - target;
    this.paceErrEma = this.paceErrEma * 0.9 + err * 0.1;
    const leaning = Math.max(-8, Math.min(8, this.paceErrEma));
    this.pace = 1 + leaning * 0.0008;
  }

  /**
   * Avance la simulation (drain des actions, snapshot en attente, pas de temps
   * cadencé) jusqu'à `now`, sur sa PROPRE horloge (`lastSimAt`), séparée de
   * l'horloge d'affichage rAF. Appelée par chaque frame ET par le tick de
   * secours du heartbeat quand le compositeur affame le rAF (mesuré chez le
   * propriétaire : gels d'affichage de 2,6 s pendant lesquels le thread JS bat
   * à 87 ms — la voiture peut donc continuer d'être pilotée et les inputs de
   * partir à 60 Hz même écran figé ; au retour de l'image, pas de rafale de
   * rattrapage ni de correction, et l'adversaire ne nous voit jamais geler).
   */
  private advanceSimulation(now: number, viaWatchdog: boolean): number {
    // Math.max(0, …) : un dt NÉGATIF (horloges mélangées, horloge système
    // ajustée…) ne doit JAMAIS entrer dans l'accumulateur ni dans decayError.
    const rawSimMs = this.lastSimAt === null ? 0 : Math.max(0, now - this.lastSimAt);
    const dt = Math.min(rawSimMs / 1000, MAX_FRAME_SECONDS);
    this.lastSimAt = Math.max(now, this.lastSimAt ?? now);

    for (const action of this.input.drainActions()) {
      if (action === 'KeyR') this.pendingReset = true;
      else if (action === 'KeyT') this.panel.toggle();
      else if (action === 'KeyG') this.cycleGhost();
    }

    // A long SIM gap just ended (clamped dt — rAF et heartbeat tous deux gelés,
    // vrai blocage du thread) : the pace servo's memory is garbage — reset it
    // so it never leans on jank-polluted backlog readings.
    if (dt >= MAX_FRAME_SECONDS - 1e-9) {
      this.paceErrEma = 0;
      this.pace = 1;
    }

    // Apply the newest queued snapshot at the very start of the frame, at the
    // exact alpha the previous frame rendered with — see the snapshot case.
    if (this.queuedSnapshot) {
      const snap = this.queuedSnapshot;
      this.queuedSnapshot = null;
      this.lastSnapshotTick = snap.tick;
      const beforeOwn = this.predictor.state;
      const alpha = Math.max(0, Math.min(1, this.accumulator / (DT * this.pace)));
      this.predictor.applySnapshot(snap.cars, snap.ack, alpha);
      const after = this.predictor.state;
      // Bit-exact replay (the normal case): the own interpolation base is
      // still valid — resetting it 30×/s injected a sub-tick jump+freeze.
      const moved =
        Math.abs(after.x - beforeOwn.x) > 1e-9 ||
        Math.abs(after.y - beforeOwn.y) > 1e-9 ||
        Math.abs(after.angle - beforeOwn.angle) > 1e-9;
      if (moved) this.prevOwnState = { ...after };
      this.updatePace();
      if (snap.live) {
        this.standings = snap.live.standings;
        this.gapAheadMs = snap.live.gapAheadMs;
        this.gapBehindMs = snap.live.gapBehindMs;
      }
    }

    // Paced fixed timestep: the wall-clock length of one simulated tick is
    // DT·pace. The pace controller (updatePace) nudges it by up to ±0.64 % to
    // hold the unacked-input backlog at its healthy target — without it, the
    // inevitable crystal-clock skew between two machines (±20-200 ppm) slowly
    // inflates or starves the server's input buffer over a long race, and the
    // resorption/hold machinery degrades the feel more and more (owner: « plus
    // la course dure et plus les problèmes s'accumulent »).
    const step = DT * this.pace;
    this.accumulator += dt;
    while (this.accumulator >= step) {
      this.tick();
      this.accumulator -= step;
      if (viaWatchdog) this.repStarvedTicks++;
    }
    return dt;
  }

  private frame(now: number): void {
    if (this.stopped) return;
    const rawDtMs = this.lastFrameAt === null ? 0 : now - this.lastFrameAt;
    this.lastFrameAt = now;
    if (this.nextGapIsHidden) {
      // Absence (onglet caché) — comptée à part, jamais comme un gel du jeu.
      this.nextGapIsHidden = false;
      this.windowHadHiddenReturn = true;
      if (rawDtMs > this.repGapHidMax) this.repGapHidMax = rawDtMs;
    } else {
      if (rawDtMs > this.frameGapMaxMs) this.frameGapMaxMs = rawDtMs;
      if (rawDtMs > this.repGapVisMax) this.repGapVisMax = rawDtMs;
      if (rawDtMs > 100) {
        this.repFreezes100++;
        this.windowFreezes++;
      }
    }

    const dt = this.advanceSimulation(now, false);

    this.predictor.decayError(dt);

    // Mode éco 30 img/s (manuel OU gouverneur auto ≥ 2) : une frame rAF sur
    // deux saute le RENDU. La simulation vient de tourner à pleine cadence
    // (inputs, prédiction, chrono) et le HUD reste rafraîchi — seuls les
    // pixels sont dessinés deux fois moins souvent.
    this.renderParity = !this.renderParity;
    this.camDt += dt;
    if (effectiveHalfRate(this.display, this.ecoGovernor.level) && this.renderParity) {
      this.updateHud(now, false);
      this.updateRaceOverlay();
      this.rafId = requestAnimationFrame(() => this.frame(performance.now()));
      return;
    }
    this.renderer.setRenderScale(effectiveRenderScale(this.display, this.ecoGovernor.level));
    this.renderer.setGridEnabled(this.display.grid);
    this.renderer.setHullDebug(this.display.hullDebug);

    // Own car: interpolate between the two latest predicted states (render is
    // decoupled from the 60 Hz simulation), then add the correction offset.
    const alpha = this.accumulator / (DT * this.pace);
    const base = interpolateState(this.prevOwnState, this.predictor.state, alpha);
    const err = this.predictor.errorOffset();
    const selfDisplay: CarState = {
      x: base.x + err.x,
      y: base.y + err.y,
      angle: base.angle + err.angle,
      vx: base.vx,
      vy: base.vy,
    };

    // Remote cars, dead-reckoned on the same timeline as the own car: what is
    // on screen is what the server will adjudicate contacts on.
    const cars: RenderCar[] = [];
    const camPoints: { x: number; y: number }[] = [{ x: selfDisplay.x, y: selfDisplay.y }];
    for (const [id, remote] of this.remotes) {
      const s = this.predictor.remoteDisplay(id, alpha);
      if (!s) continue;
      cars.push({
        x: s.x,
        y: s.y,
        angle: s.angle,
        design: remote.info.design,
        pseudo: remote.info.pseudo,
        isSelf: false,
        connected: remote.info.connected,
      });
      camPoints.push({ x: s.x, y: s.y });
    }
    cars.push({
      x: selfDisplay.x,
      y: selfDisplay.y,
      angle: selfDisplay.angle,
      design: this.selfInfo.design,
      pseudo: this.selfInfo.pseudo,
      isSelf: true,
      connected: true,
    });

    // Time-attack ghost: replayed against the local lap clock, parked on the
    // start line while no lap is armed. Visual only — never in camPoints (it
    // must not flip the camera to spectacle mode nor drag its frame).
    if (this.ghostScope !== null && this.race.state === 'waiting') {
      const data = this.ghostCache.get(this.ghostScope);
      if (data) {
        const pose =
          this.lap.startTick !== null
            ? ghostPoseAt(data, (this.tickCount - 1 + alpha - this.lap.startTick) * MS_PER_TICK)
            : ghostStartPose(data);
        if (pose) {
          cars.push({
            x: pose.x,
            y: pose.y,
            angle: pose.angle,
            design: this.ghostDesign,
            pseudo: `👻 ${data.pseudo}`,
            isSelf: false,
            connected: true,
            ghost: true,
          });
        }
      }
    }

    // Solo: follow camera with speed dezoom. Several cars: spectacle camera.
    // Seed the incoming camera from the outgoing one — no cut on transitions.
    const mode: 'follow' | 'spectacle' = camPoints.length > 1 ? 'spectacle' : 'follow';
    if (mode !== this.cameraMode) {
      if (mode === 'spectacle') {
        this.spectacleCamera.seed(this.followCamera.x, this.followCamera.y, this.followCamera.zoom);
      } else {
        this.followCamera.seed(
          this.spectacleCamera.x,
          this.spectacleCamera.y,
          this.spectacleCamera.zoom,
        );
      }
      this.cameraMode = mode;
    }
    let camera: CameraLike;
    if (camPoints.length > 1) {
      // Le lissage exponentiel des caméras est indépendant de la cadence : en
      // mode éco il reçoit le dt CUMULÉ depuis le dernier rendu (camDt).
      this.spectacleCamera.update(
        camPoints,
        this.canvas.width,
        this.canvas.height,
        this.renderer.effectiveDpr,
        this.camDt,
      );
      camera = this.spectacleCamera;
    } else {
      const cfg = this.surfaces[this.predictor.surface];
      this.followCamera.update(selfDisplay, cfg.maxSpeed, this.camDt);
      camera = this.followCamera;
    }
    this.camDt = 0;

    this.renderer.render(cars, camera);
    this.minimap.render(
      cars.map((c) => ({ x: c.x, y: c.y, color: c.design.body, isSelf: c.isSelf })),
    );
    this.updateHud(now, true);
    this.updateRaceOverlay();

    this.rafId = requestAnimationFrame(() => this.frame(performance.now()));
  }

  private tick(): void {
    if (this.pendingReset) {
      this.pendingReset = false;
      this.connection.send({ type: 'reset' });
      // Same rule as the server: back to the last crossed checkpoint when a
      // lap is running, nearest centerline otherwise.
      if (this.lap.startTick !== null) {
        const targetS = this.lap.nextCp > 0 ? (this.checkpoints[this.lap.nextCp - 1] as number) : 0;
        const idx = Math.round(targetS / this.track.step) % this.track.samples.length;
        const smp = this.track.samples[idx] as TrackSample;
        this.predictor.predictReset({
          x: smp.x,
          y: smp.y,
          angle: Math.atan2(smp.ty, smp.tx),
          vx: 0,
          vy: 0,
        });
        this.lap.prevS = smp.s;
      } else {
        this.predictor.predictReset();
        this.lap.prevS = closestPoint(this.track, this.predictor.state).s;
      }
      this.prevOwnState = { ...this.predictor.state };
    }

    // Grid + countdown: the controls are dead (the server neutralizes them too,
    // so the prediction stays exact). Inputs are still numbered and sent.
    const input: CarInput = this.controlsLocked ? NEUTRAL_INPUT : this.input.sample();
    this.prevOwnState = { ...this.predictor.state };
    const packet = this.predictor.tick(input);
    this.connection.send({ type: 'input', inputs: packet });
    this.tickCount++;

    // Reuse the wall query the predictor just computed (same post-advance,
    // pre-pairs semantics as the server's lap timing) — one coarse centerline
    // scan per tick instead of two (audit multijoueur F8).
    // A ghost fetch postponed by the 1/s server rate limit fires when allowed.
    if (this.ghostFetchQueued !== null && performance.now() - this.ghostFetchSentAt >= 1100) {
      this.requestGhost(this.ghostFetchQueued);
    }

    const q = this.predictor.lastOwnQuery ?? closestPoint(this.track, this.predictor.state);
    this.lap.headingDot =
      Math.cos(this.predictor.state.angle) * q.tangent.x +
      Math.sin(this.predictor.state.angle) * q.tangent.y;
    this.updateLap(q.s);
    this.panel.notifyDrivenSurface(this.predictor.surface);
  }

  /** G key: off → record ghost → personal ghost → off. */
  private cycleGhost(): void {
    const order: (GhostScope | null)[] = [null, 'record', 'personal'];
    const next = order[(order.indexOf(this.ghostScope) + 1) % order.length] ?? null;
    this.ghostScope = next;
    if (next === null) {
      this.hud.toast('👻 Fantôme désactivé');
      return;
    }
    const cached = this.ghostCache.get(next);
    if (cached === undefined) this.requestGhost(next);
    else this.announceGhost(cached, next);
  }

  private requestGhost(scope: GhostScope): void {
    const now = performance.now();
    if (now - this.ghostFetchSentAt < 1100) {
      this.ghostFetchQueued = scope;
      return;
    }
    this.ghostFetchSentAt = now;
    this.ghostFetchQueued = null;
    this.connection.send({ type: 'ghostGet', scope });
  }

  private announceGhost(ghost: GhostData | null, scope: GhostScope): void {
    if (ghost) {
      const label = scope === 'record' ? 'record du circuit' : 'votre record';
      this.hud.toast(`👻 Fantôme (${label}) : ${ghost.pseudo} — ${formatLapTime(ghost.timeMs)}`);
    } else {
      this.hud.toast(
        scope === 'personal'
          ? '👻 Aucun tour enregistré pour vous sur ce circuit'
          : '👻 Aucun tour enregistré sur ce circuit',
      );
    }
  }

  private updateLap(s: number): void {
    const lap = this.lap;
    const cps = this.checkpoints;
    const L = this.track.length;
    const ds = forwardArcDelta(lap.prevS, s, L);

    if (Math.abs(ds) > TELEPORT_ARC) {
      lap.startTick = null;
      lap.nextCp = 0;
      lap.prevS = s;
      return;
    }

    lap.forwardEma = lap.forwardEma * 0.95 + ds * 0.05;

    if (ds > 0) {
      while (
        lap.nextCp < cps.length &&
        crossedGateForward(lap.prevS, ds, cps[lap.nextCp] as number, L)
      ) {
        lap.nextCp++;
      }
      if (lap.prevS + ds >= L) {
        // Restart the live display; the official time arrives via 'lapDone'.
        lap.startTick = ds > 1e-9 ? this.tickCount - s / ds : this.tickCount;
        lap.nextCp = 0;
      }
    } else if (ds < 0) {
      if (lap.prevS + ds < 0) {
        lap.startTick = null;
        lap.nextCp = 0;
      }
      while (
        lap.nextCp > 0 &&
        crossedGateBackward(lap.prevS, ds, cps[lap.nextCp - 1] as number, L)
      ) {
        lap.nextCp--;
      }
    }
    lap.prevS = s;
  }

  private updateHud(now: number, rendered: boolean): void {
    const state = this.predictor.state;
    const speed = Math.hypot(state.vx, state.vy);
    this.hud.setSpeed(speed);
    this.hud.setSurface(this.predictor.surface);
    this.hud.setPing(this.connection.pingMs);
    this.hud.setNetSync(this.predictor.pendingLength, this.pace);

    const currentMs =
      this.lap.startTick !== null ? (this.tickCount - this.lap.startTick) * MS_PER_TICK : null;
    this.hud.setLapLive(currentMs);
    this.hud.setWrongWay(this.lap.forwardEma < -1.2 && this.lap.headingDot < -0.2 && speed > 60);

    if (this.predictor.pendingLength > this.repFileMax) {
      this.repFileMax = this.predictor.pendingLength;
    }
    // Le compteur FPS ne compte que les frames RENDUES : en mode éco la ligne
    // affiche honnêtement ~30 FPS, pas la cadence rAF.
    if (rendered) this.frameCounter++;
    if (now - this.fpsWindowStart >= 500) {
      if (this.fpsWindowStart > 0) {
        // Tas JS si exposé (Chrome) : la réponse en direct à « est-ce que des
        // données s'accumulent ? » sur la machine du joueur qui teste.
        const mem = (performance as { memory?: { usedJSHeapSize: number } }).memory;
        const heapMb = mem ? mem.usedJSHeapSize / 1024 / 1024 : null;
        const windowMs = now - this.fpsWindowStart;
        const fps = (this.frameCounter * 1000) / windowMs;
        this.updateAutoEco(now, fps, windowMs);
        const ecoNote =
          this.display.auto && this.ecoGovernor.level > 0
            ? `éco auto ${this.ecoGovernor.level}`
            : undefined;
        this.hud.setFps(fps, this.frameGapMaxMs, heapMb, ecoNote);
        this.repFpsSum += fps;
        this.repFpsWindows++;
        if (fps < this.repFpsMin) this.repFpsMin = fps;
        if (heapMb !== null && heapMb > this.repHeapMaxMb) this.repHeapMaxMb = heapMb;
      }
      this.fpsWindowStart = now;
      this.frameCounter = 0;
      this.frameGapMaxMs = 0;
    }

    // Rapport de fluidité vers le serveur (~30 s) : les playtests laissent des
    // données lisibles a posteriori au lieu d'un « ça saccadait » de mémoire.
    if (this.reportStartAt === null) {
      this.reportStartAt = now;
    } else if (now - this.reportStartAt >= 30_000) {
      this.sendPerfReport(now);
    }
  }

  /**
   * Gouverneur éco : jugé une fois par fenêtre FPS (500 ms). Une fenêtre est
   * mauvaise si un gel > 100 ms s'y trouve ou si la cadence RENDUE tombe sous
   * 80 % de l'attendu (60, ou 30 en mode éco). Les fenêtres contenant un
   * retour d'onglet caché ne sont pas jugées (fausse alerte garantie).
   */
  private updateAutoEco(now: number, renderedFps: number, windowMs: number): void {
    const hadHiddenReturn = this.windowHadHiddenReturn;
    const freezes = this.windowFreezes;
    this.windowHadHiddenReturn = false;
    this.windowFreezes = 0;
    if (!this.display.auto) {
      this.ecoGovernor.clear();
      return;
    }
    if (hadHiddenReturn) return;
    const expectedFps = effectiveHalfRate(this.display, this.ecoGovernor.level) ? 30 : 60;
    const bad = freezes > 0 || renderedFps < expectedFps * 0.8;
    this.ecoGovernor.judge(now, bad, windowMs);
  }

  /** Envoie les agrégats de fluidité au serveur et repart pour une période. */
  private sendPerfReport(now: number): void {
    const start = this.reportStartAt ?? now;
    const mem = (performance as { memory?: { usedJSHeapSize: number } }).memory;
    const clamp = (v: number, min: number, max: number): number =>
      Number.isFinite(v) ? Math.min(Math.max(v, min), max) : min;
    this.connection.send({
      type: 'perfReport',
      seconds: clamp((now - start) / 1000, 1, 600),
      fpsMean: clamp(this.repFpsWindows > 0 ? this.repFpsSum / this.repFpsWindows : 0, 0, 1000),
      fpsMin: clamp(this.repFpsWindows > 0 ? this.repFpsMin : 0, 0, 1000),
      gapMaxVisibleMs: clamp(this.repGapVisMax, 0, 600_000),
      gapMaxHiddenMs: clamp(this.repGapHidMax, 0, 86_400_000),
      freezes100: Math.round(clamp(this.repFreezes100, 0, 100_000)),
      timerGapMaxMs: clamp(this.repTimerGapMax, 0, 600_000),
      longTasks: Math.round(clamp(this.repLongTasks, 0, 100_000)),
      longTaskMaxMs: clamp(this.repLongTaskMax, 0, 600_000),
      starvedTicks: Math.round(clamp(this.repStarvedTicks, 0, 100_000)),
      ecoLevel: Math.round(clamp(this.display.auto ? this.ecoGovernor.level : 0, 0, 10)),
      heapMb: clamp(mem ? mem.usedJSHeapSize / 1024 / 1024 : -1, -1, 100_000),
      heapMaxMb: clamp(this.repHeapMaxMb, -1, 100_000),
      fileMax: Math.round(clamp(this.repFileMax, 0, 10_000)),
      dpr: clamp(window.devicePixelRatio || 1, 0, 10),
      canvasW: Math.round(clamp(this.canvas.width, 0, 20_000)),
      canvasH: Math.round(clamp(this.canvas.height, 0, 20_000)),
      cores: Math.round(clamp(navigator.hardwareConcurrency ?? 0, 0, 1024)),
      deviceMemory: clamp((navigator as { deviceMemory?: number }).deviceMemory ?? -1, -1, 1024),
      ua: navigator.userAgent.slice(0, 120),
    });
    this.reportStartAt = now;
    this.repFpsSum = 0;
    this.repFpsWindows = 0;
    this.repFpsMin = Infinity;
    this.repGapVisMax = 0;
    this.repGapHidMax = 0;
    this.repFreezes100 = 0;
    this.repHeapMaxMb = -1;
    this.repFileMax = 0;
    this.repTimerGapMax = 0;
    this.repLongTasks = 0;
    this.repLongTaskMax = 0;
    this.repStarvedTicks = 0;
  }

  /** Countdown digits + live race HUD, refreshed every frame. */
  private updateRaceOverlay(): void {
    if (this.race.state === 'countdown') {
      const now = this.clock.serverTick();
      const ticksToGo = now === null ? Infinity : Math.max(0, this.race.goTick - now);
      this.raceUi.showCountdown(Number.isFinite(ticksToGo) ? ticksToGo : 3 * TICK_RATE);
    }

    if (this.race.state === 'racing' && this.standings.length > 0) {
      const mine = this.standings.find((s) => s.id === this.selfId);
      if (mine) {
        this.hud.setRaceInfo({
          pos: mine.pos,
          total: this.standings.length,
          lap: Math.min(Math.max(mine.laps + 1, 1), this.race.laps),
          laps: this.race.laps,
          gapAheadMs: this.gapAheadMs,
          gapBehindMs: this.gapBehindMs,
          finished: mine.finished,
        });
      }
    } else if (this.race.state !== 'racing') {
      this.hud.setRaceInfo(null);
    }
  }

  private playersForHud(): {
    pseudo: string;
    color: string;
    connected: boolean;
    isSelf: boolean;
  }[] {
    const list = [
      {
        pseudo: this.selfInfo.pseudo,
        color: this.selfInfo.design.body,
        connected: true,
        isSelf: true,
      },
    ];
    for (const remote of this.remotes.values()) {
      list.push({
        pseudo: remote.info.pseudo,
        color: remote.info.design.body,
        connected: remote.info.connected,
        isSelf: false,
      });
    }
    return list;
  }
}

/** Interpolates two car states for display (kept for potential reuse). */
export function interpolateState(a: CarState, b: CarState, t: number): CarState {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    angle: lerpAngle(a.angle, b.angle, t),
    vx: a.vx + (b.vx - a.vx) * t,
    vy: a.vy + (b.vy - a.vy) * t,
  };
}
